import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import { Skeleton } from "@/components/Shared";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const GST_RE = /\s+(CGST|SGST|IGST|Tax)$/i;

export const FeeTypeMatrix = () => {
  const [data, setData] = useState(null);
  const [mergeGst, setMergeGst] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/tools/fee-types").then(({ data }) => setData(data));
  }, []);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/tools/fee-types/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `amazon-mis-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("MIS report downloaded (2 sheets: GST-merged + detailed)");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const matrix = useMemo(() => {
    if (!data) return null;
    const months = [...data.months].sort();
    const map = {};
    for (const r of data.rows) {
      const name = mergeGst ? r.fee.replace(GST_RE, "").trim() : r.fee;
      map[name] = map[name] || { total: 0 };
      map[name][r.month] = (map[name][r.month] || 0) + r.amount;
      map[name].total += r.amount;
    }
    const fees = Object.entries(map).sort((a, b) => Math.abs(b[1].total) - Math.abs(a[1].total));
    const feeTotals = { total: 0 };
    for (const [, vals] of fees) {
      for (const m of months) feeTotals[m] = (feeTotals[m] || 0) + (vals[m] || 0);
      feeTotals.total += vals.total;
    }
    const revTotal = months.reduce((s, m) => s + (data.revenue?.[m] || 0), 0);
    return { months, fees, feeTotals, revenue: data.revenue || {}, revTotal };
  }, [data, mergeGst]);

  if (!data) return <Skeleton className="h-64" />;
  if (!matrix || matrix.fees.length === 0) {
    return <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-lg">No settlement fee data yet — settlement reports ingest automatically (Amazon Tools → Settlement Analysis → "Fetch settlement reports").</p>;
  }

  const Cell = ({ v }) => v == null || v === 0 ? <span className="text-muted-foreground">—</span>
    : v > 0 ? <span className="text-green-600 font-medium">+{inr(v)}</span>
      : <span>{inr(Math.abs(v))}</span>;

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="fee-type-matrix">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold tracking-tight">Monthly MIS — fees vs revenue</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Straight from Amazon's official settlement reports (~3 months history, grows forward). Green = reimbursements/credits.</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Merge GST into parent fee
            <Switch checked={mergeGst} onCheckedChange={setMergeGst} data-testid="fee-gst-toggle" />
          </label>
          <Button size="sm" variant="outline" className="rounded-full" onClick={exportExcel} disabled={exporting} data-testid="mis-export-button">
            <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {exporting ? "Preparing…" : "Export to Excel"}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="fee-type-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Particular</th>
              {matrix.months.map((m) => <th key={m} className="text-right px-4 py-3 font-semibold">{m}</th>)}
              <th className="text-right px-4 py-3 font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border bg-blue-50/50 dark:bg-blue-950/20" data-testid="mis-revenue-row">
              <td className="px-4 py-2.5 font-heading font-bold">Settled Revenue (Principal)</td>
              {matrix.months.map((m) => <td key={m} className="px-4 py-2.5 text-right font-semibold tabular-nums">{inr(matrix.revenue[m] || 0)}</td>)}
              <td className="px-4 py-2.5 text-right font-heading font-bold tabular-nums">{inr(matrix.revTotal)}</td>
            </tr>
            <tr className="border-t border-border bg-muted/40" data-testid="mis-total-fees-row">
              <td className="px-4 py-2.5 font-heading font-bold">Total Fees</td>
              {matrix.months.map((m) => <td key={m} className="px-4 py-2.5 text-right font-semibold tabular-nums">{inr(Math.abs(matrix.feeTotals[m] || 0))}</td>)}
              <td className="px-4 py-2.5 text-right font-heading font-bold tabular-nums">{inr(Math.abs(matrix.feeTotals.total))}</td>
            </tr>
            <tr className="border-t border-border bg-muted/40" data-testid="mis-fee-pct-row">
              <td className="px-4 py-2.5 font-heading font-bold">Fee % of Revenue</td>
              {matrix.months.map((m) => (
                <td key={m} className="px-4 py-2.5 text-right font-semibold tabular-nums">
                  {matrix.revenue[m] ? `${(Math.abs(matrix.feeTotals[m] || 0) / matrix.revenue[m] * 100).toFixed(1)}%` : "—"}
                </td>
              ))}
              <td className="px-4 py-2.5 text-right font-heading font-bold tabular-nums">
                {matrix.revTotal ? `${(Math.abs(matrix.feeTotals.total) / matrix.revTotal * 100).toFixed(1)}%` : "—"}
              </td>
            </tr>
            {matrix.fees.map(([name, vals]) => (
              <tr key={name} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`fee-row-${name}`}>
                <td className="px-4 py-2.5 font-medium">{name}</td>
                {matrix.months.map((m) => <td key={m} className="px-4 py-2.5 text-right tabular-nums"><Cell v={vals[m]} /></td>)}
                <td className="px-4 py-2.5 text-right font-heading font-bold tabular-nums"><Cell v={vals.total} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
